/**
 * CcqClientPrefs — Batch #14.311 : paramètres d'affichage du rapport CCQ remis au client (par dossier) :
 * cases à cocher par ligne (afficher / masquer), lignes à 0 masquées automatiquement, cumulatif annuel, détail par salarié, répartition par chantier.
 * Les montants des lignes masquées restent dans le total (choix user) — avertissement si une ligne masquée a un montant ce mois-ci.
 */
import { useState } from "react";
import axios from "axios";
import { Save, AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, fmtCAD, Btn, err } from "../_shared";

const OPTIONS = [
  ["hide_zero", "Masquer automatiquement les lignes à 0 (mois et cumulatif)"],
  ["show_ytd", "Colonne « Cumulatif annuel »"],
  ["show_employees", "Détail par salarié (heures · salaire)"],
  ["show_sites", "Répartition par chantier"],
];

export default function CcqClientPrefs({ masterId, detail, onSaved, onClose }) {
  const [prefs, setPrefs] = useState({ ...detail.prefs, hidden: [...(detail.prefs.hidden || [])] });
  const [busy, setBusy] = useState(false);
  const tp = "paie-ccq-client-prefs";
  const hiddenSet = new Set(prefs.hidden);
  const toggleLine = (code) => setPrefs((p) => ({ ...p, hidden: hiddenSet.has(code) ? p.hidden.filter((c) => c !== code) : [...p.hidden, code] }));
  const isZero = (code) => Math.abs(detail.declared[code] || 0) < 0.005 && Math.abs(detail.ytd_computed?.[code] || 0) < 0.005;
  const hiddenWithAmount = detail.all_fields.filter((f) => hiddenSet.has(f.code) && Math.abs(detail.declared[f.code] || 0) >= 0.005);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/payroll/${masterId}/ccq/client/prefs`, prefs, { headers: authH() });
      toast.success("Paramètres du rapport client enregistrés");
      onSaved();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3" data-testid={tp}>
      <div className="text-xs font-bold text-slate-800">Paramètres du rapport remis au client — s'appliquent à tous les mois de ce dossier</div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {OPTIONS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" className="rounded" checked={!!prefs[k]} onChange={(e) => setPrefs((p) => ({ ...p, [k]: e.target.checked }))} data-testid={`${tp}-${k}`} />{l}
          </label>
        ))}
      </div>
      <div>
        <div className="text-[11px] font-semibold text-slate-600 mb-1">Lignes du sommaire — cochée = affichée au client (montant du mois entre parenthèses)</div>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 max-h-72 overflow-y-auto pr-1">
          {detail.all_fields.map((f) => {
            const autoHidden = prefs.hide_zero && isZero(f.code) && !hiddenSet.has(f.code);
            return (
              <label key={f.code} className={`flex items-start gap-2 text-xs ${autoHidden ? "text-slate-400" : "text-slate-700"}`} title={autoHidden ? "Masquée automatiquement (montant 0)" : ""}>
                <input type="checkbox" className="rounded mt-0.5" checked={!hiddenSet.has(f.code)} onChange={() => toggleLine(f.code)} data-testid={`${tp}-line-${f.code}`} />
                <span>{f.label} <span className="font-mono text-[10px] text-slate-500">({fmtCAD(detail.declared[f.code] || 0)})</span>{autoHidden && <span className="text-[10px] italic"> · auto-masquée (0)</span>}</span>
              </label>
            );
          })}
        </div>
      </div>
      {hiddenWithAmount.length > 0 && (
        <p className="text-[11px] text-amber-800 flex items-start gap-1.5" data-testid={`${tp}-warning`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{hiddenWithAmount.length} ligne(s) masquée(s) avec un montant ce mois-ci ({fmtCAD(hiddenWithAmount.reduce((a, f) => a + (detail.declared[f.code] || 0), 0))}) — ces montants restent inclus dans le total remis au client.</span>
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Btn tone="ghost" onClick={onClose} testId={`${tp}-cancel`}>Fermer</Btn>
        <Btn tone="orange" icon={Save} onClick={save} busy={busy} testId={`${tp}-save`}>Enregistrer</Btn>
      </div>
    </div>
  );
}
